import { ICategoriesLoader } from './i-category-loader';
import { Language } from '../../dto/language';
import { TranslationMode } from '../../dto/translation-mode';
import type { CategoryDto } from '../../dto/categories';
import { MakeReactive, noopMakeReactive } from '../make-reactive';
import { INewsContentService } from '../../services/i-news-content-service';

interface CategoryLoaderState {
	isLoading: boolean;
	isLoaded: boolean;
	categories: CategoryDto[];
	userCategories?: CategoryDto[];
	error: string;
}

export class CategoryLoader extends ICategoriesLoader {
	private readonly state: CategoryLoaderState;

	constructor(
		service: INewsContentService,
		sourceLang: Language,
		targetLang: Language,
		mode: TranslationMode,
		makeReactive: MakeReactive<unknown> = noopMakeReactive
	) {
		super(service, sourceLang, targetLang, mode, makeReactive);
		this.state = makeReactive({
			isLoading: false,
			isLoaded: false,
			categories: [],
			userCategories: undefined,
			error: '',
		}) as CategoryLoaderState;
	}

	isLoading(): boolean {
		return this.state.isLoading;
	}

	get categories(): CategoryDto[] {
		return this.state.categories;
	}

	get userCategories(): CategoryDto[] | undefined {
		return this.state.userCategories;
	}

	get error(): string {
		return this.state.error;
	}

	async loadCategories(): Promise<void> {
		if (this.state.isLoading || this.state.isLoaded) return;

		try {
			this.state.isLoading = true;
			this.state.error = '';

			const response = await this.service.getCategories({
				sourceLang: this.sourceLang,
				targetLang: this.targetLang,
				mode: this.mode,
			}, undefined);

			this.state.categories = response.categories;
			this.state.userCategories = response.userCategories;
			this.state.isLoaded = true;
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error loading categories';
			this.state.error = errorMessage;
			// eslint-disable-next-line no-console
			console.error('Error loading categories:', error);
		} finally {
			this.state.isLoading = false;
		}
	}

	clearCache(): void {
		this.state.categories = [];
		this.state.userCategories = undefined;
		this.state.isLoading = false;
		this.state.isLoaded = false;
		this.state.error = '';
	}
}


